/**
 * 感知仿真评估报表页（v2：业务 → 检查项 → 类别，design D15）。
 *
 * 页面三层：
 *   ① 业务页签（OD / OCC / TLD / Lane / 通用…，只显示 Job 数据里实际出现的业务，
 *      来自 ``/perception/report/overview``）；
 *   ② 页签内的检查项总览表（类型 / 运行 / 通过 / 不通过 / 通过率 / 主要指标）；
 *   ③ 检查项胶囊切换栏 + 选中检查项的详情。
 *
 * 详情两种：
 * - 指标型：沿用 v1 的纵向顺序（版本信息 → 漏斗 → 总体 KPI → 分类别 → Δ 四档分桶 →
 *   分 scene_group 统计 → 逐 case Δ → case 明细），数据来自
 *   ``single`` / ``compare`` / ``case-detail``，带 ``task`` / ``checker`` 参数。
 *   选了基线 Job 时打 ``compare``，**所有对比数值只在两侧 case 的交集上计算**。
 * - 判定型：版本信息 → ``checker-cases`` 的不通过 case 列表（可切换显示全部）。
 *
 * jobId / baseJobId / task / checker 都落到 URL，报表链接可直接分享；默认选中第一个
 * 业务的第一个检查项（后端已按「指标型在前」排好）。
 *
 * OCC Benchmark：``occ / occ_benchmark_check`` 先拉 ``occ-benchmark``，有 ``extras.halo`` 数据
 * （``available``）时渲染 halo 风格报表（``OccBenchmarkReport``），否则（老数据）回落到上面的
 * 通用指标型详情，行为与改动前一致。
 *
 * 数值单位口径见 ``./format.ts``：比率是 0~1 小数（展示 ×100），``delta*`` 已是 pp。
 * ``null`` 一律渲染 ``—``，不是 ``0.00%``。IoU 口径按计数单位：cell 为 IoU，box 为平均 IoU。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Select, Space, Spin, Tabs, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import {
  getJobList,
  getEvaluationFileUrls,
  getPerceptionCaseDetail,
  getPerceptionCheckerCases,
  getPerceptionCompareReport,
  getPerceptionOccBenchmark,
  getPerceptionOdFpfn,
  getPerceptionTldState,
  getPerceptionLanePolyline,
  type LanePolylineReport as LanePolylineReportData,
  type TldStateReport as TldStateReportData,
  getPerceptionOverview,
  getPerceptionSingleReport,
  type JobItem,
  type OccBenchmarkReport as OccBenchmarkReportData,
  type OdFpfnReport as OdFpfnReportData,
  type PerceptionCaseDetailRow,
  type PerceptionCheckerCaseRow,
  type PerceptionCompareReport,
  type PerceptionDeltaBucket,
  type PerceptionJobHeader,
  type PerceptionOverview,
  type PerceptionSingleReport,
} from '@/apis/simulation';
import { useTaskLogModal } from '@components/TaskLogModal';

import CaseDeltaTable from './components/CaseDeltaTable';
import CaseDetailTable from './components/CaseDetailTable';
import { CompareCategoryTable, SingleCategoryTable } from './components/CategoryTable';
import CheckerCaseTable from './components/CheckerCaseTable';
import {
  CheckerOverviewTable,
  CheckerSwitcher,
  checkerKindTag,
} from './components/CheckerOverview';
import DeltaBucketCards from './components/DeltaBucketCards';
import Funnel from './components/Funnel';
import OccBenchmarkReport from './components/OccBenchmarkReport';
import OdFpfnReport from './components/OdFpfnReport';
import OccRoiCheckerCaseTable from './components/OccRoiCheckerCaseTable';
import TldStateReport from './components/TldStateReport';
import LanePolylineReport from './components/LanePolylineReport';
import OverallKpis from './components/OverallKpis';
import SceneGroupTable from './components/SceneGroupTable';
import SideInfo from './components/SideInfo';
import {
  buildPreviewKeys,
  chunk,
  groupDetailByCategory,
  OD_TASK,
  PREVIEW_KEY_CHUNK,
} from './caseDetailUtils';
import { unitLabel } from './format';
import './index.less';

const { Title } = Typography;

/** Job 下拉的候选条数。感知 job 量级远小于 PNC，200 条足够覆盖可比范围。 */
const JOB_OPTION_LIMIT = 200;

/** 有 halo 数据时走 OCC Benchmark 报表的「业务 / 检查项」。 */
const OCC_BENCHMARK_TASK = 'occ';
const OCC_BENCHMARK_CHECKER = 'occ_benchmark_check';
const OD_FPFN_ISSUE_CHECKERS = new Set([
  'od_obj_infer_false_negative_checker',
  'od_obj_infer_false_positive_checker',
]);
/** OCC FP（halo 人工 ROI）checker：详情用独立的 case 级问题清单。 */
const OCC_ROI_CHECKERS = new Set([
  'occ_false_detection_checker',
  'occ_obstacle_false_detection_checker',
  'occ_road_false_checker',
  'occ_obstacle_road_false_checker',
]);

const normalizeAlgorithmVersion = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  const pathMatch = s.match(/x86_64_(.+?)-Orin/);
  return (pathMatch?.[1] ?? s)
    .replace(/^x86_64_/, '')
    .replace(/^x86\./, '')
    .replace(/^[A-Za-z_]\w*\.(?=\d{8}\.)/, '') || null;
};

/** 判定型检查项也展示与指标型一致的「版本信息」块。 */
const judgementHeader = (job?: JobItem): PerceptionJobHeader | null => {
  if (!job) return null;
  return {
    jobId: job.jobId,
    jobName: job.jobName,
    creator: job.creator,
    createTime: job.createTime,
    modelVersion: normalizeAlgorithmVersion(job.algorithmVersion),
    algorithmVersionRaw: job.algorithmVersion,
    gtVersion: null,
    criteriaVersion: null,
  };
};

const jobLabel = (j: JobItem): string =>
  j.jobName ? `${j.jobId}_${j.jobName}` : `${j.jobId}`;

interface CardProps {
  title: React.ReactNode;
  hint?: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}

function Card({ title, hint, extra, children }: CardProps) {
  return (
    <div className="pe-report-card">
      <div className="pe-report-card-title">
        {hint ? <Tooltip title={hint}>{title}</Tooltip> : title}
        {extra && <span style={{ marginLeft: 'auto', fontWeight: 400 }}>{extra}</span>}
      </div>
      {children}
    </div>
  );
}

/** 当前选中的「业务 / 检查项」。 */
interface Selection {
  task?: string;
  checker?: string;
}

/**
 * 在总览里落实选择：URL / 上次选中的业务与检查项仍存在则保留，否则回落到
 * 第一个业务的第一个检查项（业务已按 od/occ/tld/lane/…/general 排序）。
 */
const resolveSelection = (ov: PerceptionOverview | null, prev: Selection): Selection => {
  const tasks = ov?.tasks ?? [];
  const t = tasks.find((x) => x.task === prev.task) ?? tasks[0];
  if (!t) return prev;
  const c = t.checkers.find((x) => x.checker === prev.checker) ?? t.checkers[0];
  return { task: t.task, checker: c?.checker };
};

export default function PerceptionReportPage() {
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openTaskLog, modal: taskLogModalEl } = useTaskLogModal();

  const urlJobId = Number(searchParams.get('jobId')) || undefined;
  const urlBaseJobId = Number(searchParams.get('baseJobId')) || undefined;

  const [jobOptions, setJobOptions] = useState<JobItem[]>([]);
  const [jobId, setJobId] = useState<number | undefined>(urlJobId);
  const [baseJobId, setBaseJobId] = useState<number | undefined>(urlBaseJobId);
  const [sel, setSel] = useState<Selection>({
    task: searchParams.get('task') || undefined,
    checker: searchParams.get('checker') || undefined,
  });
  /** 「重新拉取」按钮的触发器：总览与详情的 effect 都依赖它。 */
  const [reloadKey, setReloadKey] = useState(0);

  const [overview, setOverview] = useState<PerceptionOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [single, setSingle] = useState<PerceptionSingleReport | null>(null);
  const [compare, setCompare] = useState<PerceptionCompareReport | null>(null);
  const [detail, setDetail] = useState<PerceptionCaseDetailRow[]>([]);
  const [checkerCases, setCheckerCases] = useState<PerceptionCheckerCaseRow[]>([]);
  /** OCC Benchmark（halo）报表；仅 available=true 时保存，否则走通用视图 */
  const [occBench, setOccBench] = useState<OccBenchmarkReportData | null>(null);
  const [odFpfnReport, setOdFpfnReport] = useState<OdFpfnReportData | null>(null);
  const [tldReport, setTldReport] = useState<TldStateReportData | null>(null);
  const [laneReport, setLaneReport] = useState<LanePolylineReportData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Δ 分档卡片的联动筛选：选中某档则下方 case 列表只留该档。 */
  const [bucketFilter, setBucketFilter] = useState<PerceptionDeltaBucket | null>(null);

  /** 详情请求序号：切换检查项很快时丢弃过期响应，避免旧检查项的数据覆盖新的。 */
  const detailSeq = useRef(0);

  // 感知 job 候选：按 task_type.sim_type='perception' 过滤，不挂 source 闸门
  // （感知 job 的 source 是 planning_perception(4)，按 daily 过滤会恒空）。
  useEffect(() => {
    let mounted = true;
    getJobList({ simType: 'perception', current: 1, pageSize: JOB_OPTION_LIMIT })
      .then((resp) => {
        if (!mounted) return;
        setJobOptions(resp.tasks);
        // URL 没带 jobId 时默认选最新一个，省掉一次手动选择
        setJobId((prev) => prev ?? resp.tasks[0]?.jobId);
      })
      .catch((err) => {
        console.error('加载感知 Job 候选失败:', err);
        if (mounted) message.error('加载感知 Job 列表失败');
      });
    return () => {
      mounted = false;
    };
  }, [message]);

  // jobId / baseJobId / task / checker 落到 URL，便于把报表链接直接发出去
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const put = (key: string, value: string | number | undefined) => {
      if (value) next.set(key, String(value));
      else next.delete(key);
    };
    put('jobId', jobId);
    put('baseJobId', baseJobId);
    put('task', sel.task);
    put('checker', sel.checker);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // searchParams 每次渲染都是新对象，放进依赖会自激循环；只跟这几个值走。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, baseJobId, sel.task, sel.checker]);

  // ① 业务 → 检查项总览
  useEffect(() => {
    if (!jobId) {
      setOverview(null);
      return;
    }
    let alive = true;
    setOverviewLoading(true);
    setOverview(null);
    setError(null);
    getPerceptionOverview(jobId, baseJobId)
      .then((ov) => {
        if (!alive) return;
        setOverview(ov);
        setSel((prev) => resolveSelection(ov, prev));
        if (!ov || ov.tasks.length === 0) setError('该 Job 没有可用的感知评测数据');
      })
      .catch((err) => {
        console.error('加载感知报表总览失败:', err);
        if (!alive) return;
        setError(
          err instanceof Error ? err.message : '加载感知报表失败，请确认该 Job 是感知仿真任务',
        );
      })
      .finally(() => {
        if (alive) setOverviewLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [jobId, baseJobId, reloadKey]);

  const currentTask = useMemo(
    () => overview?.tasks.find((t) => t.task === sel.task),
    [overview, sel.task],
  );
  const currentChecker = useMemo(
    () => currentTask?.checkers.find((c) => c.checker === sel.checker),
    [currentTask, sel.checker],
  );
  const checkerKind = currentChecker?.kind;

  // ② 选中检查项的详情：指标型拉 single/compare + case-detail，判定型拉 checker-cases
  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current;
    setBucketFilter(null);
    setSingle(null);
    setCompare(null);
    setDetail([]);
    setCheckerCases([]);
    setOccBench(null);
    setOdFpfnReport(null);
    setTldReport(null);
    setLaneReport(null);
    if (!jobId || !sel.task || !sel.checker || !checkerKind) {
      setDetailLoading(false);
      return;
    }
    const target = { task: sel.task, checker: sel.checker };
    setDetailLoading(true);
    try {
      if (target.task === 'lane' && target.checker === 'lane_polyline_check') {
        const report = await getPerceptionLanePolyline(jobId, baseJobId);
        if (seq !== detailSeq.current) return;
        setLaneReport(report);
        return;
      }
      if (target.task === 'tld' && target.checker === 'perception_tld_state_check') {
        const report = await getPerceptionTldState(jobId, baseJobId);
        if (seq !== detailSeq.current) return;
        setTldReport(report);
        return;
      }
      if (target.task === OD_TASK && OD_FPFN_ISSUE_CHECKERS.has(target.checker)) {
        // od-fpfn supplies the FP/FN summary; single supplies the reusable
        // category rows (by target_type). They are independent endpoints, so
        // fetch them together and fall back if structured facts are absent.
        const [fpfn, single, caseDetailRows] = await Promise.all([
          getPerceptionOdFpfn(jobId, target),
          getPerceptionSingleReport(jobId, target),
          getPerceptionCaseDetail(jobId, undefined, target),
        ]);
        if (seq !== detailSeq.current) return;
        if (fpfn?.available) {
          setOdFpfnReport(fpfn);
          setSingle(single);
          setDetail(caseDetailRows);
          return;
        }
      }
      if (checkerKind === 'judgement') {
        const rows = await getPerceptionCheckerCases(jobId, target);
        if (seq !== detailSeq.current) return;
        setCheckerCases(rows);
        return;
      }
      if (target.task === OCC_BENCHMARK_TASK && target.checker === OCC_BENCHMARK_CHECKER) {
        const bench = await getPerceptionOccBenchmark(jobId, baseJobId, target);
        if (seq !== detailSeq.current) return;
        if (bench?.available) {
          setOccBench(bench);
          return;
        }
        // 没有 halo 数据（老 OCC 结果）：回落到通用视图
      }
      // 明细与汇总并行拉：明细行数可达 case 数 × 类别数，串行会明显拖慢首屏。
      const [report, detailRows] = await Promise.all([
        baseJobId
          ? getPerceptionCompareReport(jobId, baseJobId, target)
          : getPerceptionSingleReport(jobId, target),
        getPerceptionCaseDetail(jobId, baseJobId, target),
      ]);
      if (seq !== detailSeq.current) return;
      if (baseJobId) setCompare(report as PerceptionCompareReport | null);
      else setSingle(report as PerceptionSingleReport | null);
      setDetail(detailRows);
    } catch (err) {
      console.error('加载检查项详情失败:', err);
      if (seq !== detailSeq.current) return;
      setError(err instanceof Error ? err.message : '加载检查项详情失败');
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }, [jobId, baseJobId, sel.task, sel.checker, checkerKind]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail, reloadKey]);

  /** OD：分类别行展开显示 case 明细（替代底部 Case 明细卡片）；其它业务保持原布局 */
  const isOd = sel.task === OD_TASK;
  const detailByCategory = useMemo(() => groupDetailByCategory(detail), [detail]);

  // ③ OD 明细的任务级预览图：对去重 taskId 分批（避免 GET 请求行超长）并行换签名 URL，
  //    与任务列表预览列同源；某批失败只丢该批的图，其余照常显示。
  const [previewUrlMap, setPreviewUrlMap] = useState<Record<number, string>>({});
  const [previewLoading, setPreviewLoading] = useState(false);
  useEffect(() => {
    setPreviewUrlMap({});
    if (!isOd || detail.length === 0) {
      setPreviewLoading(false);
      return;
    }
    const { keys, keyToTaskId } = buildPreviewKeys(detail);
    if (keys.length === 0) {
      setPreviewLoading(false);
      return;
    }
    let alive = true;
    setPreviewLoading(true);
    // 关掉拦截器的逐批全局报错，失败由下方汇总成一条提示
    Promise.allSettled(
      chunk(keys, PREVIEW_KEY_CHUNK).map((part) =>
        getEvaluationFileUrls(part, 'output', { skipErrorHandler: true }),
      ),
    )
      .then((results) => {
        if (!alive) return;
        const map: Record<number, string> = {};
        let failed = 0;
        results.forEach((res) => {
          if (res.status === 'rejected') {
            failed += 1;
            console.error('加载 OD 预览图 URL 失败:', res.reason);
            return;
          }
          res.value.forEach((f) => {
            const tid = keyToTaskId[f.path];
            if (tid && f.url) map[tid] = f.url;
          });
        });
        setPreviewUrlMap(map);
        if (failed > 0) {
          message.warning(`部分预览图加载失败（${failed}/${results.length} 批），其余数据不受影响`);
        }
      })
      .finally(() => {
        if (alive) setPreviewLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [isOd, detail, message]);

  const renderCaseRows = (rows: PerceptionCaseDetailRow[], inline: boolean) => (
    <CaseDetailTable
      rows={rows}
      showDelta={Boolean(baseJobId)}
      onViewLog={openTaskLog}
      unit={unit}
      {...(inline ? { hideCategory: true, previewUrlMap, previewLoading } : {})}
    />
  );
  const categoryExpand = isOd
    ? {
        renderExpanded: (category: string) =>
          renderCaseRows(detailByCategory.get(category) ?? [], true),
        hasExpanded: (category: string) => detailByCategory.has(category),
      }
    : {};

  const jobSelectOptions = useMemo(
    () => jobOptions.map((j) => ({ value: j.jobId, label: jobLabel(j) })),
    [jobOptions],
  );
  // 基线候选排除本次 job：自己跟自己比恒为 0，只会占一格
  const baseSelectOptions = useMemo(
    () => jobSelectOptions.filter((o) => o.value !== jobId),
    [jobSelectOptions, jobId],
  );

  /** 分档筛选后的逐 case Δ 列表。 */
  const filteredCaseDeltas = useMemo(() => {
    const all = compare?.caseDeltas || [];
    return bucketFilter ? all.filter((c) => c.bucket === bucketFilter) : all;
  }, [compare, bucketFilter]);

  const selectTask = (task: string) => {
    const t = overview?.tasks.find((x) => x.task === task);
    setSel({ task, checker: t?.checkers[0]?.checker });
  };
  const selectChecker = (checker: string) => setSel((prev) => ({ ...prev, checker }));

  const header = compare?.current ?? single?.header;
  const funnel = compare?.current.funnel ?? single?.funnel;
  const overall = compare?.current.overall ?? single?.overall;
  const unit = compare?.unit ?? single?.unit ?? currentChecker?.unit ?? null;
  const taskName = currentTask?.taskCn || currentTask?.task;

  const renderMetricsDetail = () => {
    if (!single && !compare) {
      return !detailLoading && <div className="pe-report-empty">该检查项暂无指标数据</div>;
    }
    return (
      <>
        {/* ① 对比双方基本信息 */}
        <Card
          title="版本信息"
          hint="模型版本取 job.algorithm_version（平台无独立感知模型版本字段）；判据配置版本库中暂无"
        >
          {compare ? (
            <div className="pe-sides">
              <SideInfo header={compare.base} side="base" />
              <SideInfo header={compare.current} side="current" />
            </div>
          ) : (
            header && (
              <div className="pe-sides" style={{ gridTemplateColumns: '1fr' }}>
                <SideInfo header={header} side="current" />
              </div>
            )
          )}
        </Card>

        {/* ② 有效 case 漏斗 */}
        {funnel && (
          <Card
            title="有效 case 漏斗"
            hint="指标只算在漏斗末级的 case 上；每一级落差都是一类被排除的原因"
            extra={
              <span style={{ fontSize: 12, color: '#6b6f76' }}>
                统计单位：{unitLabel(unit)}
                {compare &&
                  ` ｜ 基线侧：提交 ${compare.base.funnel.submitted} · 回传 ${compare.base.funnel.withMetrics} · 真值 ${compare.base.funnel.gtLoaded}`}
              </span>
            }
          >
            <Funnel funnel={funnel} taskName={taskName} />
          </Card>
        )}

        {/* ③ 总体 KPI */}
        {overall && (
          <Card
            title="总体指标"
            hint="全部 case 合并口径：计数求和、比率由计数重算（Σtp/Σgt），不是各 case 比率的平均"
          >
            <OverallKpis overall={overall} delta={compare?.delta} unit={unit} />
          </Card>
        )}

        {/* ④ 表1 / 表2：分类别 */}
        <Card
          title="分类别指标"
          hint={`${isOd ? '点击 ▶ 展开查看该类别下逐 case 明细与预览图；' : ''}ALL 行是算法侧上报的合计，与各类别并列存放；存在未分类目标时它未必等于各类别之和，以 ALL 为准`}
        >
          {compare ? (
            <CompareCategoryTable rows={compare.byCategory} {...categoryExpand} />
          ) : (
            single && (
              <SingleCategoryTable rows={single.byCategory} unit={unit} {...categoryExpand} />
            )
          )}
        </Card>

        {/* ⑤⑥⑦ Δ 段：仅对比视图 */}
        {compare && (
          <>
            <Card
              title="Case 级 Δ 召回率分档"
              hint="点击卡片可筛选下方 case 列表；档位边界 ±2pp，与后端 classify_delta 严格对应"
            >
              <DeltaBucketCards
                buckets={compare.deltaBuckets}
                active={bucketFilter}
                onSelect={setBucketFilter}
              />
            </Card>

            <Card
              title="分场景分组统计"
              hint="分组维度是 scene_group（单值分区），不是逗号拼接的 label 原文"
            >
              <SceneGroupTable rows={compare.bySceneGroup} />
            </Card>

            <Card
              title="逐 case Δ 召回率"
              extra={
                bucketFilter && (
                  <Button size="small" type="link" onClick={() => setBucketFilter(null)}>
                    清除档位筛选
                  </Button>
                )
              }
            >
              <CaseDeltaTable rows={filteredCaseDeltas} />
            </Card>
          </>
        )}

        {/* ⑧ case × 类别明细：OD 已并入分类别表的展开行，其它业务仍单独成卡 */}
        {!isOd && (
          <Card
            title="Case 明细"
            hint="下钻按钮以 taskId 为键（mcap / 日志的对象 key 都是 <taskId>/... 前缀）"
          >
            {renderCaseRows(detail, false)}
          </Card>
        )}
      </>
    );
  };

  const selectedJob = jobOptions.find((j) => j.jobId === jobId);
  const currentJudgementHeader = judgementHeader(selectedJob);

  const renderJudgementDetail = () =>
    currentChecker && (
      <>
      {/* 判定型也先展示版本信息，避免换检查项后信息块消失 */}
      {currentJudgementHeader && (
        <Card
          title="版本信息"
          hint="模型版本取本次 Job 的 algorithm_version（归一成裸版本号）；真值标注 / 判据配置版本平台暂无独立字段"
        >
          <div className="pe-sides" style={{ gridTemplateColumns: '1fr' }}>
            <SideInfo header={currentJudgementHeader} side="current" />
          </div>
        </Card>
      )}

      <Card
        title={OCC_ROI_CHECKERS.has(sel.checker || '') ? 'OCC FP case 问题清单' : '不通过的 case'}
        hint={
          OCC_ROI_CHECKERS.has(sel.checker || '')
            ? '一行一个 case：回答哪个 case 失败、偶发还是连续、发生在哪几帧；warning 是通过但有告警，skip 不进通过率分母'
            : '判定型检查项只有通过 / 不通过和说明；名单里有但没有结果行的场景按「名单 − 失败」视为通过'
        }
        extra={
          <span style={{ fontSize: 12, color: '#6b6f76' }}>
            运行 {currentChecker.runCases} · 通过 {currentChecker.passCases} · 不通过{' '}
            {currentChecker.failCases}
          </span>
        }
      >
        {OCC_ROI_CHECKERS.has(sel.checker || '') ? (
          <OccRoiCheckerCaseTable
            rows={checkerCases}
            loading={detailLoading}
            onViewLog={openTaskLog}
          />
        ) : (
          <CheckerCaseTable rows={checkerCases} loading={detailLoading} onViewLog={openTaskLog} />
        )}
      </Card>
      </>
    );

  return (
    <div className="pe-report-page">
      <div className="pe-report-header">
        <Title level={5} style={{ margin: 0, lineHeight: 1.3 }}>
          感知仿真评估报表
        </Title>
        <Space size={8}>
          <span style={{ fontSize: 12, color: '#6b6f76' }}>本次 Job</span>
          <Select
            size="small"
            value={jobId}
            options={jobSelectOptions}
            onChange={setJobId}
            placeholder="选择感知仿真 Job"
            style={{ minWidth: 240 }}
            showSearch
            optionFilterProp="label"
          />
          <span style={{ fontSize: 12, color: '#6b6f76' }}>基线 Job</span>
          <Select
            size="small"
            value={baseJobId}
            options={baseSelectOptions}
            onChange={setBaseJobId}
            placeholder="不选则只看单 Job"
            style={{ minWidth: 240 }}
            allowClear
            onClear={() => setBaseJobId(undefined)}
            showSearch
            optionFilterProp="label"
          />
          <Tooltip title="重新拉取">
            <Button size="small" icon={<ReloadOutlined />} onClick={() => setReloadKey((k) => k + 1)} />
          </Tooltip>
        </Space>
      </div>

      {error && <Alert type="warning" showIcon message={error} />}

      <Spin spinning={overviewLoading}>
        {!jobId || !overview || overview.tasks.length === 0 ? (
          <div className="pe-report-empty">
            {jobId ? '暂无感知评测数据' : '请选择一个感知仿真 Job'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* 业务页签：只显示本 Job 实际出现的业务 */}
            <Tabs
              className="pe-task-tabs"
              activeKey={sel.task}
              onChange={selectTask}
              items={overview.tasks.map((t) => ({
                key: t.task,
                label: (
                  <span>
                    {t.taskCn || t.task}
                    <span className="pe-task-count">{t.checkers.length} 个检查项</span>
                  </span>
                ),
              }))}
            />

            {currentTask && (
              <>
                <Card
                  title={`检查项总览 · ${currentTask.taskCn || currentTask.task}`}
                  hint="运行 case：名单里有该检查项或有它结果的场景数（不适用的场景不计入）；通过 = 运行 − 上报失败"
                  extra={
                    <span style={{ fontSize: 12, color: '#6b6f76' }}>
                      {currentTask.checkers.length} 个检查项 · {currentTask.runCases} 个运行 case
                      （共放入 {currentTask.submitted}）
                    </span>
                  }
                >
                  <CheckerOverviewTable
                    checkers={currentTask.checkers}
                    selected={sel.checker}
                    onSelect={selectChecker}
                    withBase={Boolean(baseJobId)}
                  />
                </Card>

                <CheckerSwitcher
                  checkers={currentTask.checkers}
                  selected={sel.checker}
                  onSelect={selectChecker}
                />
              </>
            )}

            {currentChecker && (
              <div className="pe-ck-title">
                <code className="pe-ck-code">{currentChecker.checker}</code>
                {currentChecker.checkerCn && currentChecker.checkerCn !== currentChecker.checker && (
                  <span style={{ color: '#6b6f76' }}>{currentChecker.checkerCn}</span>
                )}
                {checkerKindTag(currentChecker.kind)}
              </div>
            )}

            <Spin spinning={detailLoading}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {checkerKind === 'metrics' &&
                  (laneReport ? (
                    <LanePolylineReport report={laneReport} onViewLog={openTaskLog} />
                  ) : tldReport ? (
                    <TldStateReport report={tldReport} onViewLog={openTaskLog} />
                  ) : occBench ? (
                    <OccBenchmarkReport report={occBench} onViewLog={openTaskLog} />
                  ) : (
                    renderMetricsDetail()
                  ))}
                {odFpfnReport && (
                  <OdFpfnReport
                    report={odFpfnReport}
                    byCategory={single?.byCategory || []}
                    caseDetailRows={detail}
                    previewUrlMap={previewUrlMap}
                    previewLoading={previewLoading}
                    onViewLog={openTaskLog}
                  />
                )}
                {checkerKind === 'judgement' && !odFpfnReport && renderJudgementDetail()}
              </div>
            </Spin>
          </div>
        )}
      </Spin>

      {taskLogModalEl}
    </div>
  );
}
